import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  Ipv4Group,
  Ipv4GroupPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

// ============ Interfaces para tareas Celery ============
export interface CeleryTaskStatus {
  task_id: string;
  status: 'PENDING' | 'STARTED' | 'PROGRESS' | 'SUCCESS' | 'FAILURE' | 'RETRY' | 'REVOKED';
  info?: {
    current?: number;
    total?: number;
    percent?: number;
    status?: string;
    ips_created?: number;
    routers_associated?: number;
    mensaje?: string;
  };
  result?: {
    status: string;
    message: string;
    total_created?: number;
    routers_associated?: number;
    grupo_ipv4_id?: string;
    [key: string]: any;
  };
  error?: string;
}

export interface CreateIpv4GroupResponse {
  code: number;
  status: string;
  message: string;
  data: Ipv4Group;
  task_id?: string;
  async_processing?: boolean;
}

///* tanSctack query
export const useFetchIpv4Groups = (
  params?: GetIpv4GroupsParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['ipv4Groups', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getIpv4Groups(params);
    },
    enabled,
    refetchOnWindowFocus: false
  });
};

export const useGetIpv4Group = (id: string) => {
  return useQuery({
    queryKey: ['ipv4Groups', id],
    queryFn: async () => {
      return getIpv4Group(id);
    },
    retry: false,
    refetchOnWindowFocus: false
  });
};

export const useCreateIpv4Group = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createIpv4Group,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv4Groups'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo IPv4 creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateIpv4Group = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateIpv4Group,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv4Groups'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo IPv4 actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveIpv4Group = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeIpv4Group,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv4Groups'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetIpv4GroupsParams = Partial<Ipv4Group> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateIpv4GroupParams = Omit<Ipv4Group, 'id_grupoipv4'>;
export type UpdateIpv4GroupParams = {
  id: string;
  data: CreateIpv4GroupParams;
};

export const getIpv4Groups = async (params?: GetIpv4GroupsParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<Ipv4GroupPagingRes>(
    `${urlBackend}api/v1/grupo-ipv-4/?${queryParams}`,
    true
  );
};

export const getIpv4Group = async (id: string) => {
  try {
    return await get<Ipv4Group>(`${urlBackend}api/v1/grupo-ipv-4/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createIpv4Group = async (data: CreateIpv4GroupParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Ipv4Group>(`${urlBackend}api/v1/grupo-ipv-4/`, data, true);
};

export const updateIpv4Group = async ({ id, data }: UpdateIpv4GroupParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Ipv4Group>(`${urlBackend}api/v1/grupo-ipv-4/${id}/`, data, true);
};

export const removeIpv4Group = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Ipv4Group>(
    `${urlBackend}api/v1/grupo-ipv-4/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

// ============ Celery Task Status ============

/**
 * Hook para consultar el estado de una tarea Celery.
 * Usa polling automático mientras la tarea está en progreso.
 */
export const useFetchCeleryTaskStatus = <T = CeleryTaskStatus>(
  taskId: string | null,
  enabled = true
) => {
  return useQuery<T>({
    queryKey: ['celery-task-status', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID is required');
      return getCeleryTaskStatus<T>(taskId);
    },
    enabled: enabled && !!taskId,
    refetchInterval: (query) => {
      const taskStatus = query.state.data as any;

      if (
        taskStatus?.status === 'PENDING' ||
        taskStatus?.status === 'STARTED' ||
        taskStatus?.status === 'PROGRESS'
      ) {
        return 1500;
      }

      return false;
    },
  });
};


/**
 * Obtiene el estado de una tarea Celery.
 * El endpoint de Celery devuelve directamente el objeto CeleryTaskStatus sin wrapper ApiResponse.
 */
export const getCeleryTaskStatus = async <T>(taskId: string): Promise<T> => {
  // El endpoint de Celery devuelve directamente el objeto, no en formato ApiResponse
  const response = await get<T>(
    `${urlBackend}api/v1/celery/task/${taskId}/status/`,
    true
  );
  // La respuesta viene directamente del endpoint, hacer cast
  return response as unknown as T;
};

/**
 * Crea un grupo IPv4 y retorna la respuesta con task_id para seguimiento.
 */
export const createIpv4GroupAsync = async (data: CreateIpv4GroupParams): Promise<CreateIpv4GroupResponse> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  try {
    // La respuesta incluye code, status, message, data, task_id y async_processing
    const response = await post<Ipv4Group>(`${urlBackend}api/v1/grupo-ipv-4/`, data, true);
    // Hacer cast del response ya que incluye propiedades adicionales del backend
    return response as unknown as CreateIpv4GroupResponse;
  } finally {
    setIsGlobalLoading(false);
  }
};

/**
 * Hook para crear un grupo IPv4 con seguimiento asíncrono.
 * Retorna el task_id para poder hacer polling del progreso.
 */
export const useCreateIpv4GroupAsync = ({
  onTaskStarted,
  onSuccess,
  onError,
}: {
  onTaskStarted?: (taskId: string, groupData: Ipv4Group) => void;
  onSuccess?: (result: CeleryTaskStatus['result']) => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createIpv4GroupAsync,
    onSuccess: (response) => {
      if (response.async_processing && response.task_id) {
        // La tarea está siendo procesada en segundo plano
        onTaskStarted?.(response.task_id, response.data);
      } else {
        // Procesamiento síncrono completado
        queryClient.invalidateQueries({ queryKey: ['ipv4Groups'] });
        onSuccess?.({ status: 'success', message: response.message });
      }
    },
    onError: (error) => {
      handleAxiosError(error);
      onError?.(error instanceof Error ? error.message : 'Error desconocido');
    },
  });
};
